import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';

interface FallbackProps {
  error: Error;
  componentStack: string | null;
  eventId: string | null;
  resetError: () => void;
}

/**
 * Fallback UI component shown when an error occurs
 */
const ErrorFallback = ({ error, resetError, eventId }: FallbackProps) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Oops! Something went wrong
        </h1>

        <p className="text-gray-600 text-center mb-6">
          We're sorry for the inconvenience. An error has occurred and has been
          reported to our team.
        </p>

        {import.meta.env.DEV && (
          <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-2">
              Error Details:
            </p>
            <p className="text-xs text-red-600 font-mono break-all">
              {error.message}
            </p>
            {eventId && (
              <p className="text-xs text-gray-500 mt-2">
                Event ID: {eventId}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            onClick={resetError}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-md transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Try Again
          </button>

          <button
            onClick={() => {
              window.location.href = '/';
            }}
            className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-lg border-2 border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Go to Home
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
};

/**
 * Sentry Error Boundary wrapper component
 * Automatically catches React errors and reports them to Sentry
 */
export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: ReactNode }) => <>{children}</>,
  {
    fallback: ErrorFallback as any,
    showDialog: false, // We handle this manually in the fallback
    beforeCapture: (scope) => {
      // Add additional context before sending to Sentry
      scope.setContext('errorBoundary', {
        caught: true,
      });

      // Set error level
      scope.setLevel('error');
    },
  }
);

/**
 * Alternative: Use Sentry.ErrorBoundary directly in your components
 * Example usage:
 * 
 * import * as Sentry from '@sentry/react';
 * 
 * <Sentry.ErrorBoundary fallback={<YourCustomFallback />}>
 *   <YourComponent />
 * </Sentry.ErrorBoundary>
 */

export default SentryErrorBoundary;

